import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';

import { useAppState } from '../context/LoadTestContext';
import { fetchCalibrationRevisions, fetchSensorRecords, previewCalibration } from '../services/api';
import { unitsFor, useUnitSystem, depthInputValue, forceInputValue, parseDepthInput, parseForceInput } from './units';
import {
  LVDT_UNITS, PORTS, TEST_LOADS, blankGauge, draftDiffers, draftFromSettings, isFilled,
  mergeSensorRecords, settingsFromDraft, withNextSlot, withPorts,
} from './calibrationSettings';
import { savedMessage, useCalibrationCommit } from './useCalibrationCommit';
import UnitsToggle from './UnitsToggle';

// The Calibration window: the test's revisions down the left, the settings of
// the one selected on the right. Picking a revision shows its settings; from
// there it can be applied as it is, or edited and saved as the next revision.
// Either can be previewed on the dashboard first. A sensor's existing
// calibration record is never changed from here; saving does add a strain gauge
// the sensor records don't have yet.
//
// The dashboard's units switch lives in the title bar. It is the account's
// display setting, not part of a revision, so it works even for a test that
// has no record (and so no revisions) yet.

const formatWhen = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
};

// Trailing zeros off a default shown as a placeholder (141.47, not 141.470000).
const tidy = (n) => (Number.isFinite(n) ? String(Number(n.toPrecision(6))) : '');

// One row of the gauge table and its header share these columns.
const gaugeGrid = {
  display: 'grid',
  gridTemplateColumns: '72px minmax(120px, 1.4fr) minmax(84px, 1fr) minmax(96px, 1fr) 64px 32px',
  columnGap: 1,
  alignItems: 'center',
};
// On a phone the "LVDT n" label takes its own line above the three fields, so
// the serial number and unit aren't squeezed.
const lvdtGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 0.85fr) 122px', sm: '64px minmax(0, 1.2fr) minmax(0, 1fr) 120px' },
  columnGap: 1,
  rowGap: 0.25,
  alignItems: 'center',
};
const lvdtLabelCell = { gridColumn: { xs: '1 / -1', sm: 'auto' } };

function SectionTitle({ children, hint }) {
  return (
    <Stack direction='row' alignItems='baseline' spacing={1} sx={{ mt: 2.5, mb: 1 }}>
      <Typography variant='subtitle2' component='h3' fontWeight={700}>{children}</Typography>
      {hint && <Typography variant='caption' color='text.secondary'>{hint}</Typography>}
    </Stack>
  );
}

function ColumnLabel({ children, align }) {
  return <Typography variant='caption' color='text.secondary' sx={{ textAlign: align || 'left' }}>{children}</Typography>;
}

function CalibrationDialog({ open, onClose, onDone }) {
  const { loadTest, selectedBox, strainGaugeOptions, calPreview, setCalPreview, applyDashboardData, calView } = useAppState();
  const { save, apply } = useCalibrationCommit();
  const units = unitsFor(useUnitSystem());
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const [revisions, setRevisions] = useState(null); // null while loading
  const [defaults, setDefaults] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null); // 'preview' | 'save' | 'apply' | 'reload'
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // what the last reload from the sensor records did

  const testId = loadTest?.id;

  // Load the revisions each time the window opens. A preview in progress comes
  // back as the draft it was, so "keep editing" carries on where it left off.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setRevisions(testId ? null : []);
    setDraft(null);
    setError(null);
    setNotice(null);
    setBusy(null);
    if (!testId) return undefined;
    fetchCalibrationRevisions(testId)
      .then((data) => {
        if (cancelled) return;
        const list = data.revisions || [];
        const resumed = calPreview && list.find((r) => r.id === calPreview.baseRevisionId);
        const start = resumed || list.find((r) => r.is_active) || list[0];
        setRevisions(list);
        setDefaults(data.defaults || null);
        setSelectedId(start ? start.id : null);
        setDraft(start ? draftFromSettings(resumed ? calPreview.settings : start.settings, units) : null);
        setNote(resumed ? calPreview.note || '' : '');
      })
      .catch((err) => {
        if (cancelled) return;
        setRevisions([]);
        setError(err.message);
      });
    return () => { cancelled = true; };
    // Only (re)load on open: calPreview and units are read once, as the window opens.
  }, [open, testId]);

  // Show typed depths and loads in the new unit when the unit system flips.
  useEffect(() => {
    setDraft((prev) => (prev ? {
      ...prev,
      loads: Object.fromEntries(Object.entries(prev.loads).map(([key, load]) => (
        [key, load.kips == null ? load : { ...load, text: forceInputValue(load.kips, units) }]
      ))),
      gauges: prev.gauges.map((g) => (g.depthM == null ? g : { ...g, depth: depthInputValue(g.depthM, units) })),
    } : prev));
  }, [units.system]);

  const base = revisions?.find((r) => r.id === selectedId) || null;
  const dirty = Boolean(base && draft && draftDiffers(draft, base.settings, units));
  const nextRev = revisions?.length ? Math.max(...revisions.map((r) => r.rev_number)) + 1 : 0;

  const selectRevision = (rev) => {
    if (dirty || busy) return;
    setSelectedId(rev.id);
    setDraft(draftFromSettings(rev.settings, units));
    setNote('');
    setError(null);
    setNotice(null);
  };

  const discardChanges = () => {
    setDraft(draftFromSettings(base.settings, units));
    setNote('');
    setError(null);
    setNotice(null);
  };

  const setLoad = (key, text) => setDraft((prev) => ({
    ...prev,
    loads: { ...prev.loads, [key]: { text, kips: parseForceInput(text, units) } },
  }));
  const setLoadcell = (changes) => setDraft((prev) => ({ ...prev, loadcell: { ...prev.loadcell, ...changes } }));
  const setLvdt = (index, changes) => setDraft((prev) => ({
    ...prev,
    lvdts: prev.lvdts.map((l, i) => (i === index ? { ...l, ...changes } : l)),
  }));
  const setGauge = (index, changes) => setDraft((prev) => ({
    ...prev,
    gauges: withPorts(withNextSlot(prev.gauges.map((g, i) => (i === index ? { ...g, ...changes } : g)))),
  }));
  const removeGauge = (index) => setDraft((prev) => ({
    ...prev,
    gauges: withPorts(withNextSlot(prev.gauges.filter((_, i) => i !== index))),
  }));

  // Typed serials that match a calibrated gauge pick up its factor too.
  const factorFor = (serial) => strainGaugeOptions.find((opt) => opt.SerialNumber === serial)?.CalibrationFactor;

  const run = async (kind, action) => {
    setBusy(kind);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  // Pull the sensors' current calibration records into the form. It only edits
  // the draft: saving it as a revision is still the user's call.
  const handleReload = () => run('reload', async () => {
    setNotice(null);
    const { settings } = await fetchSensorRecords(testId);
    const { draft: merged, changed, assigned } = mergeSensorRecords(draft, settings);
    setDraft(merged);
    if (!assigned) {
      setNotice('No sensors are assigned to this test box under Equipment → Load Testing, so there was nothing to reload.');
    } else if (changed.length === 0) {
      setNotice('The form already matches the sensor records.');
    } else {
      setNotice(`Updated from the sensor records: ${changed.join(', ')}. Review the values, then preview or save.`);
    }
    setBusy(null);
  });

  const handlePreview = () => run('preview', async () => {
    const settings = settingsFromDraft(draft);
    const data = await previewCalibration(testId, settings, calView);
    // Hold polled data back before the preview's numbers go up.
    setCalPreview({ settings, note, baseRevisionId: base.id, baseRevNumber: base.rev_number, dirty, nextRev });
    applyDashboardData(data);
    onClose();
  });

  const handleSave = () => run('save', async () => {
    const revision = await save(settingsFromDraft(draft), note);
    onDone(savedMessage(revision));
    onClose();
  });

  const handleApply = () => run('apply', async () => {
    await apply(base.id);
    onDone(`Rev ${base.rev_number} is now in use`);
    onClose();
  });

  const loading = revisions === null;
  const lvdtDefault = (unit) => (defaults ? tidy(unit === 'mV/mm' ? defaults.lvdt_sensitivity / 25.4 : defaults.lvdt_sensitivity) : '');
  const filledGauges = draft ? draft.gauges.filter(isFilled) : [];
  const anyDepth = filledGauges.some((g) => g.depthM != null);
  const missingDepth = anyDepth && filledGauges.some((g) => g.depthM == null);
  const missingFactor = filledGauges.some((g) => !String(g.factor).trim());

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      disableScrollLock
      fullWidth
      maxWidth='lg'
      fullScreen={isMobile}
      aria-labelledby='calibration-dialog-title'
    >
      <DialogTitle id='calibration-dialog-title' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='h6' component='span' fontWeight={700} noWrap sx={{ display: 'block' }}>
            Calibration{selectedBox?.Name ? ` · ${selectedBox.Name}` : ''}
          </Typography>
          {loadTest?.start_time && (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              Test started {formatWhen(loadTest.start_time)}
            </Typography>
          )}
          {/* On a phone the units switch drops under the title. */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant='caption' color='text.secondary'>Units</Typography>
            <UnitsToggle />
          </Box>
        </Box>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
            <Typography variant='caption' color='text.secondary'>Units</Typography>
            <UnitsToggle />
          </Box>
          <IconButton onClick={onClose} disabled={Boolean(busy)} aria-label='Close'><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { md: 520 } }}>
        {/* Revisions */}
        <Box
          component='nav'
          aria-label='Calibration revisions'
          sx={{
            width: { xs: '100%', md: 264 },
            flexShrink: 0,
            borderRight: { md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: 'divider',
            maxHeight: { xs: 190, md: 'none' },
            overflowY: 'auto',
          }}
        >
          <Typography variant='overline' color='text.secondary' sx={{ px: 2, pt: 1.5, display: 'block', lineHeight: 1.6 }}>
            Revisions
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
          ) : revisions.length === 0 ? (
            <Typography variant='caption' color='text.secondary' sx={{ px: 2, py: 1, display: 'block' }}>
              None yet
            </Typography>
          ) : (
            <List dense disablePadding>
              {revisions.map((rev) => {
                const selected = rev.id === selectedId;
                return (
                  <ListItemButton
                    key={rev.id}
                    selected={selected}
                    disabled={(dirty || Boolean(busy)) && !selected}
                    onClick={() => selectRevision(rev)}
                    aria-current={selected ? 'true' : undefined}
                    sx={{ px: 2, py: 1, alignItems: 'flex-start', flexDirection: 'column', gap: 0.25 }}
                  >
                    <Stack direction='row' alignItems='center' spacing={1} sx={{ width: '100%' }}>
                      <Typography variant='body2' fontWeight={600}>Rev {rev.rev_number}</Typography>
                      {rev.rev_number === 0 && <Typography variant='caption' color='text.secondary'>original</Typography>}
                      <Box sx={{ flex: 1 }} />
                      {rev.is_active && <Chip size='small' color='success' label='In use' sx={{ height: 20 }} />}
                    </Stack>
                    <Typography variant='caption' color='text.secondary'>
                      {[rev.created_by, formatWhen(rev.created_at)].filter(Boolean).join(' · ')}
                    </Typography>
                    {rev.note && rev.rev_number !== 0 && (
                      <Typography variant='caption' sx={{ overflowWrap: 'anywhere' }}>{rev.note}</Typography>
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
          {dirty && (
            <Typography variant='caption' color='text.secondary' sx={{ px: 2, py: 1, display: 'block' }}>
              Save or discard your changes to pick another revision.
            </Typography>
          )}
        </Box>

        {/* Settings of the selected revision */}
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
          <Stack spacing={1}>
            {testId ? (
              <Alert severity='info'>
                These settings apply to this test only. They do not change the sensors&apos; master calibration
                records under Equipment → Load Testing. A strain gauge that isn&apos;t in those records yet is
                added to them, with the factor entered here, when you save.
              </Alert>
            ) : (
              <Alert severity='info'>
                Calibration can be edited once this test&apos;s record is available, after the next data refresh.
                Units can be changed above.
              </Alert>
            )}
            {error && <Alert severity='error' onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity='success' onClose={() => setNotice(null)}>{notice}</Alert>}
            {dirty && (
              <Alert
                severity='warning'
                action={<Button color='inherit' size='small' onClick={discardChanges} disabled={Boolean(busy)}>Discard changes</Button>}
              >
                Edited from Rev {base.rev_number}. Not saved yet.
              </Alert>
            )}
          </Stack>

          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

          {!loading && draft && (
            <>
              <SectionTitle hint='Shown with the readings on the dashboard'>Test loads</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, maxWidth: 620 }}>
                {TEST_LOADS.map(({ key, label }) => (
                  <TextField
                    key={key}
                    size='small'
                    type='number'
                    label={label}
                    value={draft.loads[key].text}
                    onChange={(e) => setLoad(key, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0 }}
                    InputProps={{ endAdornment: <InputAdornment position='end'>{units.forceUnit}</InputAdornment> }}
                  />
                ))}
              </Box>

              <SectionTitle>Load cell</SectionTitle>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.2fr) minmax(0, 1fr)' }, gap: 1.5, maxWidth: 560 }}>
                <TextField
                  size='small'
                  label='Serial number'
                  value={draft.loadcell.serial}
                  onChange={(e) => setLoadcell({ serial: e.target.value })}
                />
                <TextField
                  size='small'
                  type='number'
                  label='Factor'
                  value={draft.loadcell.factor}
                  placeholder={defaults ? tidy(defaults.loadcell_factor) : ''}
                  onChange={(e) => setLoadcell({ factor: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ endAdornment: <InputAdornment position='end'>lbs/digit</InputAdornment> }}
                  helperText={draft.loadcell.factor === '' ? 'Blank uses the default factor' : ' '}
                />
              </Box>

              <SectionTitle hint='Blank sensitivity uses the default'>LVDTs</SectionTitle>
              <Box sx={{ maxWidth: 620 }}>
                <Box sx={{ ...lvdtGrid, mb: 0.5 }}>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
                  <ColumnLabel>Serial number</ColumnLabel>
                  <ColumnLabel>Sensitivity</ColumnLabel>
                  <ColumnLabel>Unit</ColumnLabel>
                </Box>
                <Stack spacing={1}>
                  {draft.lvdts.map((lvdt, i) => (
                    <Box key={lvdt.channel} sx={lvdtGrid}>
                      <Typography variant='body2' sx={lvdtLabelCell}>LVDT {lvdt.channel}</Typography>
                      <TextField
                        size='small'
                        value={lvdt.serial}
                        onChange={(e) => setLvdt(i, { serial: e.target.value })}
                        inputProps={{ 'aria-label': `LVDT ${lvdt.channel} serial number` }}
                      />
                      <TextField
                        size='small'
                        type='number'
                        value={lvdt.sensitivity}
                        placeholder={lvdtDefault(lvdt.unit)}
                        onChange={(e) => setLvdt(i, { sensitivity: e.target.value })}
                        inputProps={{ 'aria-label': `LVDT ${lvdt.channel} sensitivity` }}
                      />
                      <TextField
                        select
                        size='small'
                        value={lvdt.unit}
                        onChange={(e) => setLvdt(i, { unit: e.target.value })}
                        SelectProps={{ MenuProps: { disableScrollLock: true } }}
                        inputProps={{ 'aria-label': `LVDT ${lvdt.channel} sensitivity unit` }}
                      >
                        {[...new Set([...LVDT_UNITS, lvdt.unit])].map((unit) => (
                          <MenuItem key={unit} value={unit} dense>{unit}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <SectionTitle hint={`${filledGauges.length} entered · factor in microstrain/digit`}>Strain gauges</SectionTitle>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: 520, maxWidth: 760 }}>
                  <Box sx={{ ...gaugeGrid, mb: 0.5 }}>
                    <ColumnLabel>Port</ColumnLabel>
                    <ColumnLabel>Serial number</ColumnLabel>
                    <ColumnLabel>Depth ({units.depthUnit})</ColumnLabel>
                    <ColumnLabel>Factor</ColumnLabel>
                    <ColumnLabel align='center'>Exclude</ColumnLabel>
                    <span />
                  </Box>
                  <Stack spacing={1}>
                    {draft.gauges.map((gauge, i) => {
                      const filled = isFilled(gauge);
                      const label = filled && gauge.channel ? `SG${gauge.channel}` : `gauge ${i + 1}`;
                      return (
                        <Box key={`gauge_${i}`} sx={gaugeGrid}>
                          {/* Logger port = SG_digits(n). Ports taken by other rows are greyed out. */}
                          <TextField
                            select
                            size='small'
                            value={gauge.channel ?? ''}
                            disabled={!filled}
                            onChange={(e) => setGauge(i, { channel: Number(e.target.value) })}
                            SelectProps={{ MenuProps: { disableScrollLock: true, slotProps: { paper: { sx: { maxHeight: 280 } } } } }}
                            inputProps={{ 'aria-label': `Logger port for ${label}` }}
                          >
                            {PORTS.map((port) => (
                              <MenuItem
                                key={port}
                                value={port}
                                dense
                                disabled={draft.gauges.some((g, j) => j !== i && isFilled(g) && g.channel === port)}
                              >
                                {port}
                              </MenuItem>
                            ))}
                          </TextField>

                          <Autocomplete
                            size='small'
                            freeSolo
                            // The row's own remove button clears it.
                            disableClearable
                            options={strainGaugeOptions}
                            value={strainGaugeOptions.find((opt) => opt.SerialNumber === gauge.serial) || null}
                            inputValue={gauge.serial}
                            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.SerialNumber || '')}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id}>
                                <Stack>
                                  <Typography variant='body2'>{option.SerialNumber}</Typography>
                                  <Typography variant='caption' color='text.secondary'>
                                    Factor: {option.CalibrationFactor || '—'} · Tested: {option.TestDate || '—'}
                                  </Typography>
                                </Stack>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={filledGauges.length === 0 ? 'Serial number' : 'Add a gauge'}
                                inputProps={{ ...params.inputProps, 'aria-label': `Serial number for ${label}` }}
                              />
                            )}
                            onChange={(event, option) => {
                              if (option && typeof option === 'object') {
                                setGauge(i, { serial: option.SerialNumber, factor: option.CalibrationFactor || '' });
                              }
                            }}
                            onInputChange={(event, text, reason) => {
                              if (reason === 'reset') return; // handled by onChange
                              if (!text) {
                                setGauge(i, blankGauge());
                                return;
                              }
                              const known = factorFor(text);
                              setGauge(i, known !== undefined ? { serial: text, factor: known || '' } : { serial: text });
                            }}
                          />

                          <TextField
                            size='small'
                            type='number'
                            value={gauge.depth}
                            disabled={!filled}
                            onChange={(e) => setGauge(i, { depth: e.target.value, depthM: parseDepthInput(e.target.value, units) })}
                            inputProps={{ 'aria-label': `Depth of ${label}`, min: 0 }}
                          />

                          {/* Autofilled from the gauge's calibration record; typed in when none exists */}
                          <TextField
                            size='small'
                            type='number'
                            value={gauge.factor}
                            disabled={!filled}
                            onChange={(e) => setGauge(i, { factor: e.target.value })}
                            inputProps={{ 'aria-label': `Calibration factor of ${label}` }}
                            sx={filled && !String(gauge.factor).trim()
                              ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' } }
                              : undefined}
                          />

                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Checkbox
                              size='small'
                              checked={gauge.excluded}
                              disabled={!filled}
                              onChange={(e) => setGauge(i, { excluded: e.target.checked })}
                              inputProps={{ 'aria-label': `Exclude ${label}` }}
                            />
                          </Box>

                          {filled ? (
                            <Tooltip title='Remove gauge'>
                              <IconButton size='small' onClick={() => removeGauge(i)} aria-label={`Remove ${label}`}>
                                <CloseIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          ) : <span />}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
              <Stack sx={{ mt: 1 }}>
                {missingFactor && (
                  <Typography variant='caption' color='warning.main'>
                    A gauge with no factor is left off the strain graph.
                  </Typography>
                )}
                {missingDepth && (
                  <Typography variant='caption' color='text.secondary'>
                    A gauge with no depth is left off the strain graph while others have one.
                  </Typography>
                )}
              </Stack>

              <SectionTitle hint='Optional'>Note for this revision</SectionTitle>
              <TextField
                size='small'
                fullWidth
                multiline
                minRows={2}
                value={note}
                disabled={!dirty}
                placeholder={dirty ? 'What changed and why' : 'Edit a value above to save a new revision'}
                onChange={(e) => setNote(e.target.value)}
                inputProps={{ maxLength: 500, 'aria-label': 'Note for this revision' }}
                sx={{ maxWidth: 760 }}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ py: 2, px: 3, flexWrap: 'wrap', rowGap: 1 }}>
        <Button variant='text' onClick={onClose} disabled={Boolean(busy)}>Close</Button>
        {base && draft && (
          <Tooltip
            arrow
            title={(
              <>
                Fills the form with the latest calibration of each sensor assigned to this test box under
                Equipment → Load Testing: load cell, LVDTs and strain gauges. Use it after a sensor&apos;s
                record was added or corrected there.
                <br /><br />
                Test loads, gauge depths, excluded gauges and anything typed here for a sensor that isn&apos;t
                assigned to the box are left as they are. Nothing is saved until you save a revision.
              </>
            )}
          >
            <span>
              <Button
                variant='text'
                startIcon={<SyncIcon />}
                onClick={handleReload}
                disabled={Boolean(busy)}
              >
                {busy === 'reload' ? 'Reloading…' : 'Reload from sensor records'}
              </Button>
            </span>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {base && draft && (
          <>
            <Tooltip title={!dirty && base.is_active ? 'This revision is already what the dashboard shows' : ''}>
              <span>
                <Button
                  variant='outlined'
                  onClick={handlePreview}
                  disabled={Boolean(busy) || (!dirty && base.is_active)}
                >
                  {busy === 'preview' ? 'Loading preview…' : 'Preview on dashboard'}
                </Button>
              </span>
            </Tooltip>
            {dirty ? (
              <Button variant='contained' onClick={handleSave} disabled={Boolean(busy)}>
                {busy === 'save' ? 'Saving…' : `Save as Rev ${nextRev}`}
              </Button>
            ) : (
              <Button variant='contained' onClick={handleApply} disabled={Boolean(busy) || base.is_active}>
                {busy === 'apply' ? 'Applying…' : base.is_active ? `Rev ${base.rev_number} is in use` : `Apply Rev ${base.rev_number}`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CalibrationDialog;
